// cleanup_convert_to_typescript_file_BTR

/* eslint-disable import/prefer-default-export */
import {
  includes,
  __,
  map,
  isNull,
  isUndefined,
  curry,
  initial,
  last,
  pick,
  defaultTo,
  compose
} from 'lodash/fp';
import moment from 'moment';

import { DATE, DATE_FORMAT, WEEK_OF_MONTH } from 'constants.js';

export const dayOfWeekBooleanListToIntList = (arr = []) =>
  arr
    .map((x, i) => (x ? i : x))
    .filter((x) => x !== false)
    // Scheduler needs Sunday to be `7`
    .map((x) => (x === 0 ? 7 : x));

export const getFutureDated = (str) => {
  switch (str) {
    case 'AFTER':
      return true;
    case 'BEFORE':
    default:
      return false;
  }
};

export const TYPES = {
  MINUTES: 'MINUTES',
  HOURLY: 'HOURS',
  DAILY: 'DAYS',
  WEEKLY: 'WEEKS',
  MONTHLY: 'MONTHS',
  SEMIMONTHLY: 'SEMIMONTHLY',
  YEARLY: 'YEARS'
};

const { MONTHLY, SEMIMONTHLY, WEEKLY } = TYPES;
const { FIRST, FOURTH, LAST, SECOND, THIRD } = WEEK_OF_MONTH;
const isApiSendingSemiMonthly = (data) => data?.bookVO?.instruction?.daysOfMonth?.length === 2;

export const mapRecurrenceFrequencyTypeFromApi = (data) => {
  const type = data?.bookVO?.instruction?.frequency;

  switch (type) {
    case 'MONTHLY': {
      if (isApiSendingSemiMonthly(data)) return SEMIMONTHLY;

      return MONTHLY;
    }
    case 'WEEKLY':
    default:
      return WEEKLY;
  }
};

const mapRecurrenceFrequencyTypeToApi = (formData) => {
  const type = formData?.recurring?.value?.type;

  switch (type) {
    case SEMIMONTHLY:
    case MONTHLY:
      return 'MONTHLY';
    case WEEKLY:
    default:
      return 'WEEKLY';
  }
};

export const formatDateValue = (value) => moment(value).format(DATE_FORMAT);

export function convertFrequencyType(frequency) {
  switch (frequency) {
    case WEEKLY:
      return 'WEEKLY';
    case SEMIMONTHLY:
      return SEMIMONTHLY;
    case MONTHLY:
    default:
      return 'MONTHLY';
  }
}

// prettier-ignore
export const cycleForward = (arr) =>
  [last(arr), ...initial(arr)];

// prettier-ignore
export const includesDays = days =>
  map(includes(__, days), [7, 1, 2, 3, 4, 5, 6])

export function getWeekly(formData) {
  const frequency =
    formData?.recurring?.recurrence?.nWeeks || formData?.recurring?.value?.recurrence?.nWeeks;

  const dayList =
    formData?.recurring?.value?.recurrence?.onN || formData?.recurring?.recurrence?.onN;

  return {
    weeklyRecurrence: {
      frequency,
      weekDays: includesDays(dayList)
    }
  };
}

export function convertDayTextToNumber(day) {
  switch (day) {
    case FIRST:
      return 1;
    case SECOND:
      return 2;
    case THIRD:
      return 3;
    case FOURTH:
      return 4;
    case LAST:
      return 5;
    default:
      return 1;
  }
}

export function convertFrequency(type, dayType) {
  if (type === 'semimonthly') {
    return 2;
  }

  if (type === 'monthly' && dayType === 'week') {
    return 3;
  }

  // Type is monthly and day type is monthly
  return 1;
}

export function getMonthly(formData) {
  const intervalType = formData?.recurring?.value?.recurrence?.onN?.[0]?.intervalType;

  if (intervalType === DATE) {
    const dayValue = formData?.recurring?.value?.recurrence?.onN?.[0]?.day || '1';
    return {
      monthlyRecurrence: {
        frequency: convertFrequency('monthly', DATE),
        month: formData?.recurring?.value?.recurrence?.nMonths,
        day: dayValue === LAST ? 0 : Number(dayValue)
      }
    };
  }

  return {
    monthlyRecurrence: {
      // Not sure what this frequency is
      frequency: convertFrequency('monthly', 'week'),
      month: formData?.recurring?.value?.recurrence?.nMonths,
      dayOfWeek: intervalType,
      weekOfMonth: convertDayTextToNumber(
        formData?.recurring?.value?.recurrence?.onN?.[0]?.day || FIRST
      )
    }
  };
}

export function getSemiMonthly(formData) {
  if (formData?.recurring?.value?.recurring) {
    Object.assign(formData.recurring, formData.recurring.recurring);
  }

  const dayIntervalType = formData?.recurring?.value?.recurrence?.onN?.[0]?.intervalType;
  const otherDayIntervalType = formData?.recurring?.value?.recurrence?.onN?.[1]?.intervalType;
  let dayOrWeek;

  if (dayIntervalType === DATE && otherDayIntervalType === DATE) {
    dayOrWeek = {
      day: Number(formData?.recurring?.value?.recurrence?.onN?.[0]?.day || '1') || 99,
      otherDay: Number(formData?.recurring?.value?.recurrence?.onN?.[1]?.day || '1') || 99
    };
  }

  if (dayIntervalType !== DATE && otherDayIntervalType === DATE) {
    dayOrWeek = {
      dayOfWeek: dayIntervalType,
      weekOfMonth: convertDayTextToNumber(
        formData?.recurring?.value?.recurrence?.onN?.[0]?.day || FIRST
      ),
      otherDay: Number(formData?.recurring?.value?.recurrence?.onN?.[1]?.day || '1')
    };
  }

  if (dayIntervalType === DATE && otherDayIntervalType !== DATE) {
    dayOrWeek = {
      day: Number(formData?.recurring?.value?.recurrence?.onN?.[0]?.day || '1'),
      otherDayOfWeek: otherDayIntervalType,
      otherWeekOfMonth: convertDayTextToNumber(
        formData?.recurring?.value?.recurrence?.onN?.[1]?.day || FIRST
      )
    };
  }

  if (dayIntervalType !== DATE && otherDayIntervalType !== DATE) {
    dayOrWeek = {
      dayOfWeek: dayIntervalType,
      weekOfMonth: convertDayTextToNumber(
        formData?.recurring?.value?.recurrence?.onN?.[0]?.day || FIRST
      ),
      otherDayOfWeek: otherDayIntervalType,
      otherWeekOfMonth: convertDayTextToNumber(
        formData?.recurring?.value?.recurrence?.onN?.[1]?.day || FIRST
      )
    };
  }

  return {
    monthlyRecurrence: {
      frequency: convertFrequency('semimonthly', DATE),
      month: formData?.recurring?.value?.recurrence?.nSemiMonths,
      ...dayOrWeek
    }
  };
}

const whenNotEmpty = curry((fn, date) => {
  if (!isNull(date) && !isUndefined(date)) {
    return fn(date);
  }

  return date;
});

export const mapFormDataToApi = (formData) => {
  const frequencyType = mapRecurrenceFrequencyTypeToApi(formData);
  const schedulerFrequencyType = formData?.recurring?.value?.type || formData?.recurring?.type;

  return {
    ...pick(['transferId', 'memo', 'name'], formData),
    frequencyType,
    amount: `${formData?.amount}`.replace(/,/g, ''),
    debitAccountId: formData?.debitAccountId,
    creditAccountId: formData?.creditAccountId,
    futureDated: formData?.futureDated || false,
    startDate: compose(
      (mDate) => mDate.format(),
      (date) => moment(date),
      defaultTo(new Date()),
      (data) => data?.recurring?.startValue || data?.recurring?.value?.startValue
    )(formData),
    endDate: compose(
      whenNotEmpty((mDate) => mDate.format()),
      whenNotEmpty((date) => moment(date)),
      (data) => data?.recurring?.endValue || data?.recurring?.value?.endValue
    )(formData),
    ...(schedulerFrequencyType === WEEKLY && getWeekly(formData)),
    ...(schedulerFrequencyType === MONTHLY && getMonthly(formData)),
    ...(schedulerFrequencyType === SEMIMONTHLY && getSemiMonthly(formData))
  };
};

export const getIntervalType = (type) => (num) => {
  switch (type) {
    case 'WEEK_OF_MONTH':
      switch (num) {
        case 1:
          return 'MONDAY';
        case 2:
          return 'TUESDAY';
        case 3:
          return 'WEDNESDAY';
        case 4:
          return 'THURSDAY';
        case 5:
          return 'FRIDAY';
        case 0:
        default:
          return 'DAY';
      }
    default:
      return DATE;
  }
};

export const convertApiDayToScheduler = (type) => (instruction) => {
  switch (type) {
    case 'WEEK_OF_MONTH':
      switch (instruction?.week) {
        case 1:
          return FIRST;
        case 2:
          return SECOND;
        case 3:
          return THIRD;
        case 4:
          return FOURTH;
        case 5:
        default:
          return LAST;
      }
    default:
      return instruction?.daysOfMonth?.[0];
  }
};

export const buildRecurrence = (frequencyType) => (data) => {
  switch (frequencyType) {
    case SEMIMONTHLY:
      return {
        nSemiMonths: data?.instruction?.month,
        onN: [
          {
            day: data?.instruction?.daysOfMonth?.[0],
            intervalType: DATE
          },
          {
            day: data?.instruction?.daysOfMonth?.[1],
            intervalType: DATE
          }
        ]
      };
    case MONTHLY:
      return {
        nMonths: data?.instruction?.month,
        onN: [
          {
            day: convertApiDayToScheduler(data?.instruction?.frequencyOption)(data?.instruction),
            intervalType: getIntervalType(data?.instruction?.frequencyOption)(
              data?.instruction?.weekDays
                ?.map((x, i) => (x ? i : x))
                .filter((x) => x !== false)?.[0]
            )
          }
        ]
      };
    case WEEKLY:
    default:
      return {
        nWeeks: data?.instruction?.week,
        onN: dayOfWeekBooleanListToIntList(data?.instruction?.weekDays)
      };
  }
};
